<!-- HotX Console 0.1.121 首次改密导航与域名默认首页。实际修改时间：2026-09-29 13:25:25 CST；精确父基线：2b265b8475709018e4bf7099ec6a081871eb4c6b；变更：CONSOLE-HOME-DATA-PASSWORD-20260929。Google 回调继续保留一次性 state 校验。 -->
<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';
import { ElMessage } from 'element-plus';
import { useRoute, useRouter } from 'vue-router';
import { consoleApi } from '../api/client';
import {
  GOOGLE_POPUP_AUTHENTICATED_MESSAGE_TYPE,
  consumeGooglePopupState
} from '../auth/googlePopupState';
import { normalizeConsoleRedirect } from '../router';
import { useAuthStore } from '../stores/auth';

/*
 * HotX customer login boundary:
 * - Customer console supports Google OAuth and account/password login.
 * - CDNxxx account login is a formal customer credential path, not an admin impersonation shortcut.
 * - Google login must keep the original OAuth state/redirect validation intact; do not "simplify" it when changing email/account login.
 * - Admin/Ops login belongs to admin-web and must never redirect users into this console app by accident.
 */
const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const mode = ref<'login' | 'register' | 'reset'>('login');
const loading = ref(false);
const googleLoading = ref(false);
const codeLoading = ref(false);
const error = ref('');
const googleStateKey = 'hotx-cdn:console:google-oauth-state';
const googleRedirectKey = 'hotx-cdn:console:google-oauth-redirect';
const googleAuthorizationEndpoint = '/api/v1/auth/google/authorization-url';
const googleAuthorizationFallbackCodes = new Set([
  'NOT_FOUND',
  'ENDPOINT_NOT_FOUND',
  'CURRENT_VERSION_UNSUPPORTED',
  'NOT_IMPLEMENTED',
  'UNSUPPORTED_OPERATION'
]);
const form = reactive({
  email: '',
  password: '',
  customerName: '',
  verificationCode: '',
  newPassword: ''
});

const title = computed(() => {
  if (mode.value === 'register') {
    return '邮箱注册（备用）';
  }
  if (mode.value === 'reset') {
    return '找回密码';
  }
  return '账号密码登录';
});

const submitLabel = computed(() => {
  if (mode.value === 'register') {
    return '注册并登录';
  }
  if (mode.value === 'reset') {
    return '设置新密码';
  }
  return '登录';
});

onMounted(async () => {
  const code = firstQueryValue(route.query.code);
  const idToken = firstQueryValue(route.query.id_token);
  if (!code && !idToken) {
    return;
  }
  await finishGoogleLogin(code, idToken, firstQueryValue(route.query.state));
});

async function startGoogleLogin() {
  error.value = '';
  googleLoading.value = true;
  try {
    const state = createOAuthState();
    const redirect = normalizeConsoleRedirect(route.query.redirect);
    sessionStorage.setItem(googleStateKey, state);
    sessionStorage.setItem(googleRedirectKey, redirect);
    window.location.assign(await resolveGoogleAuthorizationUrl(state));
  } catch (e) {
    sessionStorage.removeItem(googleStateKey);
    sessionStorage.removeItem(googleRedirectKey);
    error.value = googleStartErrorMessage(e);
  } finally {
    googleLoading.value = false;
  }
}

async function finishGoogleLogin(code?: string, idToken?: string, state?: string) {
  error.value = '';
  googleLoading.value = true;
  try {
    const expectedState = sessionStorage.getItem(googleStateKey);
    // 更新时间：2026-09-04 17:45 CST。弹窗回调消费父窗口写入的一次性 state；整页登录继续校验当前 sessionStorage。
    const popupStateValid = consumeGooglePopupState(state);
    const pageStateValid = Boolean(expectedState && state && state === expectedState);
    if (!pageStateValid && !popupStateValid) {
      sessionStorage.removeItem(googleStateKey);
      sessionStorage.removeItem(googleRedirectKey);
      error.value = 'Google登录状态校验失败，请重新开始。';
      return;
    }
    await auth.loginWithGoogle({ code, idToken, state });
    if (popupStateValid && notifyGuestPopupAuthentication(state)) {
      return;
    }
    const redirect = auth.mustChangePassword
      ? '/account'
      : normalizeConsoleRedirect(sessionStorage.getItem(googleRedirectKey));
    sessionStorage.removeItem(googleStateKey);
    sessionStorage.removeItem(googleRedirectKey);
    await router.replace(redirect);
  } catch (e) {
    sessionStorage.removeItem(googleStateKey);
    sessionStorage.removeItem(googleRedirectKey);
    error.value = e instanceof Error ? e.message : 'Google登录失败，请使用邮箱登录。';
  } finally {
    googleLoading.value = false;
  }
}

// 来源：console-google-popup-return-20260915；CST 2026-09-15 17:46；父基线 c79cc29664c6b69410189f64679c7762310e252c。
// 仅在弹窗已通过一次性 state 校验后回传原页，避免普通整页登录被错误关闭。
function notifyGuestPopupAuthentication(state?: string) {
  if (!state || !window.opener || window.opener.closed) return false;
  window.opener.postMessage({
    type: GOOGLE_POPUP_AUTHENTICATED_MESSAGE_TYPE,
    state
  }, window.location.origin);
  window.close();
  return true;
}

async function submit() {
  error.value = '';
  loading.value = true;
  try {
    if (mode.value === 'login') {
      await auth.login({ email: form.email, password: form.password });
      ElMessage.success('登录成功，登录有效期最长 30 天');
      await router.push(auth.mustChangePassword ? '/account' : normalizeConsoleRedirect(route.query.redirect));
    } else if (mode.value === 'register') {
      await auth.register({
        email: form.email,
        password: form.password,
        verificationCode: form.verificationCode,
        customerName: form.customerName
      });
      ElMessage.success('注册成功，登录有效期最长 30 天');
      await router.push('/domains');
    } else {
      await consoleApi.auth.resetPassword({
        email: form.email,
        verificationCode: form.verificationCode,
        newPassword: form.newPassword
      });
      ElMessage.success('密码已重置，请使用新密码登录');
      mode.value = 'login';
    }
  } catch (e) {
    error.value = e instanceof Error ? e.message : '请求失败';
  } finally {
    loading.value = false;
  }
}

async function sendCode() {
  error.value = '';
  codeLoading.value = true;
  try {
    if (mode.value === 'register') {
      await consoleApi.auth.sendRegisterCode(form.email);
    } else {
      await consoleApi.auth.sendPasswordResetCode(form.email);
    }
    ElMessage.success('验证码已发送，请查收邮箱');
  } catch (e) {
    error.value = e instanceof Error ? e.message : '验证码发送失败';
  } finally {
    codeLoading.value = false;
  }
}

function createOAuthState() {
  return globalThis.crypto?.randomUUID?.() || `state-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

async function resolveGoogleAuthorizationUrl(state: string) {
  try {
    const response = await consoleApi.auth.googleAuthorizationUrl({ state });
    if (typeof response.authorizationUrl === 'string' && response.authorizationUrl.trim()) {
      return response.authorizationUrl;
    }
  } catch (e) {
    if (!shouldUseBackendGoogleAuthorizationFallback(e)) {
      throw e;
    }
  }
  return buildBackendGoogleAuthorizationUrl(state);
}

function buildBackendGoogleAuthorizationUrl(state: string) {
  const url = new URL(googleAuthorizationEndpoint, window.location.origin);
  url.searchParams.set('state', state);
  return `${url.pathname}${url.search}`;
}

function shouldUseBackendGoogleAuthorizationFallback(error: unknown) {
  const apiError = error as { code?: unknown; status?: unknown; message?: unknown };
  if (apiError.status === 404 || apiError.status === 405) {
    return true;
  }
  if (typeof apiError.code === 'string' && googleAuthorizationFallbackCodes.has(apiError.code)) {
    return true;
  }
  return typeof apiError.message === 'string'
    && apiError.message.includes('Google')
    && apiError.message.includes('尚未配置');
}

function googleStartErrorMessage(error: unknown) {
  if (
    error instanceof Error
    && !(error.message.includes('Google') && error.message.includes('尚未配置'))
  ) {
    return error.message;
  }
  return 'Google 登录暂时无法发起，请稍后重试或使用账号密码登录。';
}

function firstQueryValue(value: unknown) {
  if (Array.isArray(value)) {
    return typeof value[0] === 'string' ? value[0] : undefined;
  }
  return typeof value === 'string' ? value : undefined;
}
</script>

<template>
  <main class="login-page">
    <section class="login-card">
      <p class="eyebrow">HotX CDN</p>
      <h1>登录客户控制台</h1>
      <button
        type="button"
        class="google-login-button"
        :disabled="googleLoading"
        @click="startGoogleLogin"
      >
        <span class="google-mark" aria-hidden="true">
          <svg viewBox="0 0 24 24" focusable="false">
            <path fill="#4285f4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.4h6.5a5.6 5.6 0 0 1-2.4 3.6v2.9h3.9c2.2-2.1 3.5-5.1 3.5-8.6Z" />
            <path fill="#34a853" d="M12 24c3.2 0 5.9-1.1 7.9-3l-3.9-2.9c-1.1.7-2.4 1.1-4 1.1-3.1 0-5.7-2.1-6.6-4.9h-4v3c2 4 6 6.7 10.6 6.7Z" />
            <path fill="#fbbc04" d="M5.4 14.3A7.2 7.2 0 0 1 5 12c0-.8.1-1.6.4-2.3v-3h-4A12 12 0 0 0 0 12c0 1.9.5 3.7 1.4 5.3l4-3Z" />
            <path fill="#ea4335" d="M12 4.8c1.7 0 3.3.6 4.5 1.8L20 3.2A11.8 11.8 0 0 0 12 0C7.4 0 3.4 2.7 1.4 6.7l4 3c.9-2.8 3.5-4.9 6.6-4.9Z" />
          </svg>
        </span>
        <span>{{ googleLoading ? '正在连接 Google...' : '继续使用 Google' }}</span>
      </button>
      <p class="login-note">开始使用 HotX CDN：推荐使用 Google 登录。首次登录将自动创建客户账号并进入控制台。登录有效期最长 30 天，过期后需重新验证身份。</p>
      <div class="login-divider"><span>或使用账号密码登录（兼容邮箱登录）</span></div>
      <el-alert v-if="error" :title="error" type="error" show-icon />
      <h2>{{ title }}</h2>
      <el-form label-position="top" @submit.prevent="submit">
        <el-form-item :label="mode === 'login' ? '账号或邮箱' : '邮箱'">
          <el-input
            v-model="form.email"
            :autocomplete="mode === 'login' ? 'username' : 'email'"
            :placeholder="mode === 'login' ? 'CDN001 或 name@example.com' : 'name@example.com'"
          />
        </el-form-item>
        <el-form-item v-if="mode === 'register'" label="客户名称">
          <el-input v-model="form.customerName" />
        </el-form-item>
        <el-form-item v-if="mode !== 'reset'" label="密码">
          <el-input v-model="form.password" type="password" show-password autocomplete="current-password" />
        </el-form-item>
        <el-form-item v-if="mode !== 'login'" label="验证码">
          <div class="inline-control">
            <el-input v-model="form.verificationCode" />
            <el-button type="primary" plain :loading="codeLoading" @click.prevent="sendCode">
              {{ codeLoading ? '发送中...' : '获取验证码' }}
            </el-button>
          </div>
        </el-form-item>
        <el-form-item v-if="mode === 'reset'" label="新密码">
          <el-input v-model="form.newPassword" type="password" show-password />
        </el-form-item>
        <el-button type="primary" native-type="submit" :loading="loading" class="full-button">
          {{ submitLabel }}
        </el-button>
      </el-form>
      <div class="login-switch">
        <button @click="mode = 'login'">账号密码登录</button>
        <button @click="mode = 'register'">邮箱注册（备用）</button>
        <button @click="mode = 'reset'">忘记密码</button>
      </div>
    </section>
  </main>
</template>
